import { hashTuningConfiguration, isTuningCommand, isTuningSettingValue, TrialOperation, TuningTrialError } from '@playcastdotio/PeerCore';
import type { IFreshStreamTrials, ITuningClock, ITuningCommandDispatcher, ITuningEndpoint, TuningCapabilities,
  TuningCommand, TuningPatch, TuningReceipt, TuningSettingKey, TuningTrialSnapshot } from '@playcastdotio/PeerCore';
import type { IHostTuningRuntime } from '../HostTuningContracts';

export type TuningCommandDispatcherOptions = {
  clock: ITuningClock; host: IHostTuningRuntime; guest: () => ITuningEndpoint | undefined;
  fresh: () => IFreshStreamTrials; authorize: (command: TuningCommand) => boolean;
  snapshot: () => TuningTrialSnapshot; publish: (snapshot: TuningTrialSnapshot) => void;
  cancelEffects: (command: TuningCommand) => void;
  restoreLive: (command: TuningCommand, baseline: TuningTrialSnapshot) => Promise<void>;
  policyVersion?: string;
  hash?: typeof hashTuningConfiguration;
  authorizeFinal?: (command: TuningCommand) => boolean;
};

/** Coordinator owns early acceptance; this dispatcher returns the completed, coordinated effect. */
export class TuningCommandDispatcher implements ITuningCommandDispatcher {
  private pending: Promise<TuningReceipt> | undefined;
  private readonly replay = new Map<string, { content: string; receipt: Promise<TuningReceipt> }>();
  constructor(private readonly options: TuningCommandDispatcherOptions) {}

  execute(command: TuningCommand): Promise<TuningReceipt> {
    if (!isTuningCommand(command) || !this.options.authorize(command)) return Promise.resolve(this.failure(command, 'unauthorized', 'rejected'));
    const content = JSON.stringify(command), previous = this.replay.get(command.commandId);
    if (previous) return previous.content === content ? previous.receipt : Promise.resolve(this.failure(command, 'command-conflict', 'rejected'));
    if (this.pending) return Promise.resolve(this.failure(command, 'operation-in-progress', 'rejected'));
    const pinned = JSON.parse(content) as TuningCommand;
    const receipt = this.apply(pinned).catch(error => this.failure(pinned, error instanceof Error ? error.message : 'application failed'))
      .finally(() => { if (this.pending === receipt) this.pending = undefined; });
    this.pending = receipt; this.replay.set(command.commandId, { content, receipt });
    if (this.replay.size > 64) this.replay.delete(this.replay.keys().next().value as string);
    return receipt;
  }

  private async apply(command: TuningCommand): Promise<TuningReceipt> {
    if (command.action.kind === 'apply-live') return this.applyLive(command);
    if (command.action.kind === 'stop') {
      await this.options.fresh().stop('operator stop');
      const snapshot = this.options.snapshot();
      return this.commit(command, { ...snapshot, phase: 'stopped' }, []);
    }
    const snapshot = await this.options.fresh().execute(command);
    if (snapshot.phase !== 'settling') return this.failure(command, snapshot.error?.message ?? 'fresh replacement failed');
    if (!(this.options.authorizeFinal ?? this.options.authorize)(command)) return this.failure(command, 'Authority changed before fresh commit');
    return this.commit(command, snapshot, snapshot.receipts.flatMap(receipt => receipt.settings));
  }

  private async applyLive(command: TuningCommand): Promise<TuningReceipt> {
    if (command.action.kind !== 'apply-live') throw new TuningTrialError('invalid-action', 'Live action required');
    const guest = this.options.guest(), baseline = this.options.snapshot();
    const hostPatch = this.subset(command.action.patch, this.options.host.capabilities(command.target));
    const guestPatch = guest ? this.subset(command.action.patch, guest.capabilities()) : {};
    for (const key of Object.keys(command.action.patch) as TuningSettingKey[]) {
      if (!(key in hostPatch) && !(key in guestPatch)) throw new TuningTrialError('setting-unavailable', `${key} is not supported by either endpoint`);
    }
    const operation = new TrialOperation(this.options.clock, 10_000);
    const effects: Promise<TuningReceipt>[] = [];
    try {
      if (Object.keys(hostPatch).length) effects.push(this.options.host.apply({ ...command, action: { kind: 'apply-live', patch: hostPatch } }));
      if (guest && Object.keys(guestPatch).length) effects.push(guest.apply({ ...command, action: { kind: 'apply-live', patch: guestPatch } }));
      const receipts = await operation.within(() => Promise.all(effects), 'live endpoint effects');
      if (!this.options.authorize(command)) throw new TuningTrialError('lease-revoked', 'Authority changed before coordinated commit');
      if (receipts.some(receipt => !['applied', 'observed'].includes(receipt.stage) || receipt.settings.some(setting =>
        ['failed', 'unsupported', 'requires-fresh-stream', 'requires-host-restart'].includes(setting.status)))) {
        throw new TuningTrialError('partial-application', 'Both endpoint effects did not complete');
      }
      const effective: TuningPatch = Object.fromEntries(receipts.flatMap(receipt => receipt.settings.map(setting =>
        [setting.key, isTuningSettingValue(setting.key, setting.readback) ? setting.readback
          : isTuningSettingValue(setting.key, setting.accepted) ? setting.accepted : setting.requested])));
      const requested = { ...baseline.requested, ...command.action.patch };
      const configurationHash = await operation.within(() => (this.options.hash ?? hashTuningConfiguration)({ version: 1,
        strategyId: baseline.strategyId, policyVersion: this.options.policyVersion ?? 'production-v1', settings: requested }), 'live configuration identity');
      return this.commit(command, { ...baseline, requested, configurationHash,
        effective: { ...baseline.effective, ...effective } }, receipts.flatMap(receipt => receipt.settings));
    } catch (error) {
      this.options.cancelEffects(command);
      const restoration = new TrialOperation(this.options.clock, 2_000);
      await restoration.within(() => this.options.restoreLive(command, baseline), 'live subset restoration')
        .finally(() => restoration.cancel('completed'));
      throw error;
    } finally { operation.cancel('completed'); }
  }

  private subset(patch: TuningPatch, capabilities: TuningCapabilities): TuningPatch {
    return Object.fromEntries(Object.entries(patch).filter(([key]) => capabilities.settings[key as TuningSettingKey]?.mode === 'live'));
  }
  private commit(command: TuningCommand, snapshot: TuningTrialSnapshot, settings: TuningReceipt['settings']): TuningReceipt {
    const target = { ...snapshot.target, configRevision: command.expectedRevision + 1 };
    const receipt: TuningReceipt = { version: 1, commandId: command.commandId, target, stage: 'applied', settings, policyVersion: 'tuning-v1' };
    this.options.publish({ ...snapshot, target, receipts: [...snapshot.receipts.slice(-127).map(previous =>
      previous.commandId === command.commandId ? { ...previous, target } : previous), receipt] });
    return receipt;
  }
  private failure(command: TuningCommand, message: string, stage: 'failed' | 'rejected' = 'failed'): TuningReceipt {
    return { version: 1, commandId: command.commandId, target: command.target, stage, settings: [], policyVersion: 'tuning-v1',
      error: { code: 'tuning-command-failed', message: message.slice(0, 2_048) } };
  }
}
